import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    HStack,
    VStack,
    Text,
    Spinner,
    IconButton,
    createListCollection,
} from "@chakra-ui/react";
import { HiOutlineCpuChip, HiOutlineClipboard, HiStar } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ConversationService } from "@/services/ConversationService";
import { ModelService } from "@/services/ModelService";
import { CandidateService } from "@/services/CandidateService";
import { toaster } from "@/components/ui/toaster";
import { SpreadsheetIntegrationService } from "@/services/SpreadsheetIntegrationService";
import { fromApiResponse } from "@/services/jobOfferMapper";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import GoogleSheetsIntegrationModal from "./components/GoogleSheetsIntegrationModal";
import dayjs from "dayjs";
import {RecruitmentStatusBadge} from "@/pages/models/RecruitmentStatusBadge";
import InfoField from "./components/InfoField";
import InteractionList from "./components/InteractionList";
import useOfferPreparationPolling from "@/components/hooks/useOfferPreparationPolling";
import {isOfferPreparing, isOfferLinkLocked, offerLinkLockedKey} from "@/utils/offerPreparation";

const isDraftOrInactive = (status) => ["draft", "inactive"].includes(status?.toLowerCase());

const RecruitmentDetailPage = () => {
    const { t: tJobOffer } = useTranslation("jobOffer");
    const { t, i18n } = useTranslation("models");
    const { id, recruitmentId } = useParams();
    const navigate = useNavigate();

    const [recruitment, setRecruitment] = useState(null);
    useOfferPreparationPolling(recruitment ? [recruitment] : [], (_, patch) =>
        setRecruitment((prev) => (prev ? { ...prev, ...patch } : prev)));
    const [conversations, setConversations] = useState([]);
    const [isRecruitmentLoading, setIsRecruitmentLoading] = useState(true);
    const [isConversationsLoading, setIsConversationsLoading] = useState(true);
    const [editModalValues, setEditModalValues] = useState(null);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [isBulkCreateLoading, setIsBulkCreateLoading] = useState(false);
    const [isChangingStatus, setIsChangingStatus] = useState(false);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [convFavLoadingId, setConvFavLoadingId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [statusFilter, setStatusFilter] = useState("Finished");
    const [sortBy, setSortBy] = useState("createdat");
    const [sortDir, setSortDir] = useState("desc");
    const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [googleIntegration, setGoogleIntegration] = useState(null);
    const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);

    const statusCollection = createListCollection({
        items: [
            { label: t("conversationStatus.all"), value: "" },
            { label: t("conversationStatus.inprogress"), value: "InProgress" },
            { label: t("conversationStatus.finished"), value: "Finished" },
            { label: t("conversationStatus.holded"), value: "Holded" },
            { label: t("conversationStatus.cancelled"), value: "Cancelled" },
            { label: t("conversationStatus.qualifiedfornextstage"), value: "QualifiedForNextStage" },
            { label: t("conversationStatus.furtherselection"), value: "FurtherSelection" },
            { label: t("conversationStatus.readytohire"), value: "ReadyToHire" },
            { label: t("conversationStatus.hired"), value: "Hired" },
            { label: t("conversationStatus.rejected"), value: "Rejected" },
            { label: t("conversationStatus.candidateresigned"), value: "CandidateResigned" },
        ],
    });

    useSetPageHeader(t("recruitmentDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    const fetchIntegration = () => {
        SpreadsheetIntegrationService.getIntegrations(recruitmentId).then((list) => {
            const active = (list ?? []).find((i) => i.provider === "GoogleSheets" && i.isActive);
            setGoogleIntegration(active ?? null);
        });
    };

    const fetchRecruitment = () => ConversationService.getRecruitmentById(id, recruitmentId).then(setRecruitment);

    useEffect(() => {
        setIsRecruitmentLoading(true);
        fetchRecruitment().finally(() => setIsRecruitmentLoading(false));
        fetchIntegration();
    }, [id, recruitmentId]);

    const handleIntegrationSuccess = () => {
        setIsIntegrationModalOpen(false);
        fetchIntegration();
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setIsConversationsLoading(true);
        ConversationService.getConversations(id, recruitmentId, page, pageSize, statusFilter || null, sortBy, sortDir, showFavoriteOnly || null, debouncedSearch || null)
            .then((data) => {
                setConversations(data?.items ?? []);
                setTotalCount(data?.totalCount ?? 0);
                setSelectedIds(new Set());
            })
            .finally(() => setIsConversationsLoading(false));
    }, [id, recruitmentId, page, pageSize, statusFilter, sortBy, sortDir, showFavoriteOnly, debouncedSearch]);

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }
        setPage(1);
    };

    const toggleAll = () => {
        if (conversations.length > 0 && selectedIds.size === conversations.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(conversations.map((c) => c.id)));
        }
    };

    const handleBulkCreateFromInteraction = async () => {
        setIsBulkCreateLoading(true);
        try {
            const result = await CandidateService.bulkCreateFromInteraction(Array.from(selectedIds));
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("recruitmentDetailPage.bulkCreateSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setSelectedIds(new Set());
        } catch {
            toaster.create({ type: "error", title: t("recruitmentDetailPage.bulkCreateError") });
        } finally {
            setIsBulkCreateLoading(false);
        }
    };

    const handleDownloadXlsx = async () => {
        setIsXlsxLoading(true);
        try {
            const ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
            const response = await ConversationService.exportWorkflowCsv(
                recruitmentId,
                ids,
                statusFilter || null,
                sortBy,
                sortDir,
                showFavoriteOnly || null,
            );
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `konwersacje_${recruitmentId}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleToggleStatus = async () => {
        const newStatus = recruitment.status?.toLowerCase() === "active" ? "Inactive" : "Active";
        setIsChangingStatus(true);
        try {
            const updated = await ModelService.changeWorkflowStatus(recruitmentId, newStatus);
            setRecruitment(updated);
        } finally {
            setIsChangingStatus(false);
        }
    };

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await ModelService.toggleFavorite(recruitmentId);
        setRecruitment(updated);
        setIsFavLoading(false);
    };

    const handleToggleConversationFavorite = async (e, convId) => {
        e.stopPropagation();
        setConvFavLoadingId(convId);
        const updated = await ConversationService.toggleFavorite(convId);
        setConversations((prev) =>
            prev.map((c) => c.id === convId ? { ...c, isFavorite: updated.isFavorite } : c)
        );
        setConvFavLoadingId(null);
    };

    const handleOpenEdit = async () => {
        const data = await ModelService.getRecruitmentById(recruitmentId);
        setEditModalValues(fromApiResponse(data));
    };

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            {isRecruitmentLoading ? (
                <VStack w="full" py={8}
                        align="center"
                        justify="center"
                        minH={100}>
                    <Spinner color="purple.500" size="lg" />
                </VStack>
            ) : recruitment && (
                <Box
                    w="full"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius={12}
                    p={5}
                >
                    <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={2}>
                        <HStack>
                            <Text fontWeight={700} fontSize="lg" color="gray.900">{recruitment.jobTitle}</Text>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label="Ulubione"
                                color={recruitment?.isFavorite ? "yellow.400" : "gray.500"}
                                loading={isFavLoading}
                                onClick={handleToggleFavorite}
                            >
                                <HiStar />
                            </IconButton>
                        </HStack>
                        <HStack gap={2} flexWrap="wrap">
                            {recruitment?.status?.toLowerCase() !== "draft" &&
                                <Button variant="outlined" size="sm" onClick={handleDownloadXlsx} loading={isXlsxLoading} w={{ base: "full", md: "auto" }}>
                                    {selectedIds.size > 0
                                        ? t("recruitmentDetailPage.downloadXlsxSelected", { count: selectedIds.size })
                                        : t("recruitmentDetailPage.downloadXlsx")}
                                </Button>
                            }
                            {selectedIds.size > 0 && (
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={handleBulkCreateFromInteraction}
                                    loading={isBulkCreateLoading}
                                    w={{ base: "full", md: "auto" }}
                                >
                                    {t("recruitmentDetailPage.bulkCreateCandidates", { count: selectedIds.size })}
                                </Button>
                            )}
                            {recruitment?.status?.toLowerCase() !== "draft" &&
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={handleToggleStatus}
                                    loading={isChangingStatus}
                                    w={{ base: "full", md: "auto" }}
                                >
                                    {recruitment?.status?.toLowerCase() === "active"
                                        ? t("recruitmentDetailPage.deactivateButton")
                                        : t("recruitmentDetailPage.activateButton")}
                                </Button>
                            }
                            
                            <Button variant="outlined" size="sm" onClick={handleOpenEdit} disabled={isOfferPreparing(recruitment)} w={{ base: "full", md: "auto" }}>
                                {t("recruitmentDetailPage.editButton")}
                            </Button>
                            {recruitment?.status?.toLowerCase() !== "draft" &&
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => navigate(`/models/${id}/recruitments/${recruitmentId}/candidates`)}
                                    w={{ base: "full", md: "auto" }}
                                >
                                    {t("recruitmentDetailPage.viewCandidatesButton")}
                                </Button>
                            }
                            {/*{googleIntegration ? (*/}
                            {/*    <>*/}
                            {/*        <Button*/}
                            {/*            as="a"*/}
                            {/*            href={googleIntegration.spreadsheetUrl}*/}
                            {/*            target="_blank"*/}
                            {/*            rel="noopener noreferrer"*/}
                            {/*            variant="outlined"*/}
                            {/*            size="sm"*/}
                            {/*            w={{ base: "full", md: "auto" }}*/}
                            {/*        >*/}
                            {/*            <SiGooglesheets />*/}
                            {/*            {t("recruitmentDetailPage.openGoogleSheets")}*/}
                            {/*        </Button>*/}
                            {/*        <Button variant="outlined" size="sm" onClick={() => setIsIntegrationModalOpen(true)} w={{ base: "full", md: "auto" }}>*/}
                            {/*            {t("recruitmentDetailPage.manageIntegration")}*/}
                            {/*        </Button>*/}
                            {/*    </>*/}
                            {/*) : (*/}
                            {/*    <Button variant="outlined" size="sm" onClick={() => setIsIntegrationModalOpen(true)} w={{ base: "full", md: "auto" }}>*/}
                            {/*        {t("recruitmentDetailPage.connectGoogleSheets")}*/}
                            {/*    </Button>*/}
                            {/*)}*/}
                        </HStack>
                    </HStack>
                    <HStack gap={8} align="flex-start" flexWrap="wrap">
                        <InfoField label={t("recruitmentDetailPage.fieldPosition")}>
                            <Text color="gray.900" fontSize="md" fontWeight={700}>
                                {recruitment.jobTitle}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldStatus")}>
                            <RecruitmentStatusBadge status={recruitment.status} preparationStatus={recruitment.preparationStatus} preparationProgress={recruitment.preparationProgress} />
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCreatedAt")}>
                            <Text color="gray.900" fontSize="sm">
                                {new Date(recruitment.createdAtUtc).toLocaleDateString(i18n.language)}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCompany")}>
                            <Text color="gray.900" fontSize="sm">
                                {recruitment.companyName}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldLocation")}>
                            <Text color="gray.900" fontSize="sm">
                                {recruitment.location}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldWorkMode")}>
                            <Text color="gray.900" fontSize="sm">
                                {tJobOffer(`enums.WORK_MODE.${recruitment.workMode.toLowerCase()}`)}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldWhatsapp")}>
                            <Text color="gray.900" fontSize="sm">
                                {recruitment.whatsappNumber ?? "-"}
                            </Text>
                        </InfoField>
                        {!isDraftOrInactive(recruitment?.status) &&
                            <InfoField label={t("recruitmentDetailPage.fieldChatLink")}>
                                <HStack gap={2}>
                                    <a
                                        href={isOfferLinkLocked(recruitment) ? undefined : `/c${recruitment.chatPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={isOfferLinkLocked(recruitment) ? t(offerLinkLockedKey(recruitment)) : undefined}
                                        style={{
                                            color: "var(--chakra-colors-gray-900)",
                                            fontSize: "var(--chakra-fontSizes-sm)",
                                            ...(isOfferLinkLocked(recruitment) ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                                        }}
                                    >
                                        Chat link
                                    </a>
                                    <IconButton
                                        variant="ghost"
                                        size="xs"
                                        aria-label={t("recruitmentDetailPage.copyLink")}
                                        onClick={() => navigator.clipboard.writeText(window.location.origin + `/c${recruitment.chatPath}`)}
                                        disabled={isOfferLinkLocked(recruitment)}
                                        color="gray.500"
                                        _hover={{ color: "gray.900" }}>
                                       <HiOutlineClipboard />
                                    </IconButton>
                                </HStack>
                            </InfoField>
                        }
                    </HStack>
                </Box>
            )}

            <GoogleSheetsIntegrationModal
                isOpen={isIntegrationModalOpen}
                onClose={() => setIsIntegrationModalOpen(false)}
                workflowId={recruitmentId}
                integration={googleIntegration}
                onSuccess={handleIntegrationSuccess}
            />

            <InteractionList
                conversations={conversations}
                isLoading={isConversationsLoading}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setPage}
                onPageSizeChange={(ps) => { setPageSize(ps); setPage(1); }}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                showFavoriteOnly={showFavoriteOnly}
                onFavoriteToggle={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                statusFilter={statusFilter}
                onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
                statusCollection={statusCollection}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleAll={toggleAll}
                convFavLoadingId={convFavLoadingId}
                onToggleFavorite={handleToggleConversationFavorite}
                onRowClick={(conv) => navigate(`/models/${id}/recruitments/${recruitmentId}/conversations/${conv.id}`)}
            />
            {editModalValues && (
                <JobOfferFormModal
                    onClose={() => setEditModalValues(null)}
                    initialValues={editModalValues}
                    onSubmit={(values, isDraft) =>
                        isDraft
                            ? ModelService.saveDraft(values).then(fetchRecruitment)
                            : ModelService.updateRecruitment(recruitmentId, values).then(() => {
                                  fetchRecruitment();
                                  setEditModalValues(null);
                              })
                    }
                />
            )}
        </VStack>
    );
};

export default RecruitmentDetailPage;
